// client/src/components/StudentDetailModal.jsx
// Read-focused detail view for a Student. Opens on any row tap from
// StudentsTab — gives the manager the full picture (full card number,
// notes, dates, identity chips) without forcing a jump into the edit
// form. From here a single tap escalates to edit or archive.
//
// The hero band uses the single brand gradient for every student — no
// per-student colour (it read as loud and arbitrary).

import { useState, useEffect } from 'react';
import Modal from './Modal.jsx';
import Spinner from './Spinner.jsx';
import CreditPointsForm from './CreditPointsForm.jsx';
import {
  PencilSquareIcon,
  ArchiveBoxXMarkIcon,
  IdentificationIcon,
  TagIcon,
  CalendarDaysIcon,
  ChatBubbleIcon,
  BanknotesIcon,
} from './Icons.jsx';
import { formatDate, formatDateTime } from '../lib/format.js';
import { formatAmount } from '../lib/currency.js';
import { useTransactions } from '../hooks/useTransactions.js';
import { useCamp } from '../hooks/useCamps.js';
import { useStudentBalance } from '../hooks/useStudents.js';

export default function StudentDetailModal({ student, campId, onClose, onEdit, onArchive }) {
  // Hooks must run unconditionally — call before the null guard. Balance is
  // cheap so it loads eagerly; the heavier history list loads only after the
  // manager taps "הצג היסטוריה" (saves a server pull on every card open).
  const camp = useCamp(campId);
  const balanceQ = useStudentBalance(campId, student?.id, { enabled: !!student?.id });
  const [showHistory, setShowHistory] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const txQ = useTransactions(
    campId,
    { studentId: student?.id, limit: 100 },
    { enabled: !!student?.id && showHistory }
  );

  // Collapse the panels whenever a different student opens.
  useEffect(() => {
    setShowHistory(false);
    setAdjusting(false);
  }, [student?.id]);

  if (!student) return null;
  const s = student;
  const initials = ((s.firstName?.[0] || '') + (s.lastName?.[0] || '')).toUpperCase();
  const hasCard = !!s.cardNumber;
  const currency = camp.data?.currency;
  const balance = balanceQ.data;
  const txs = txQ.data?.transactions ?? [];

  return (
    <>
    <Modal open={!!student} onClose={onClose} title="כרטיס תלמיד" maxWidth="max-w-xl">
      {/* HERO — gradient band that bleeds full width of the modal body
          via -mx-6 -mt-5. Avatar lives INSIDE the band next to the name
          (single profile composition) so there's no orphan avatar block
          floating below a redundant name. */}
      <div
        className="-mx-6 -mt-5 mb-5 px-6 py-6 text-white bg-brand-gradient
                   relative overflow-hidden"
      >
        <div className="absolute -bottom-16 -end-12 w-44 h-44 rounded-full bg-white/10" />
        <div className="absolute top-2 -start-8 w-24 h-24 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-4">
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-white/15 backdrop-blur-sm
                       text-white grid place-items-center text-2xl sm:text-3xl font-extrabold
                       ring-2 ring-white/40 shrink-0 shadow-pop"
          >
            {initials || '·'}
          </div>
          <div className="min-w-0">
            {/* No eyebrow — the modal header already says "כרטיס תלמיד" right
                above this band, so a second label here would just repeat. */}
            <h2 className="text-2xl sm:text-3xl font-medium drop-shadow truncate">
              {s.firstName} {s.lastName}
            </h2>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {s.className && (
                <span className="badge bg-white/20 text-white border border-white/30 backdrop-blur-sm">
                  {s.className}
                </span>
              )}
              <span
                className={`badge backdrop-blur-sm border ${
                  hasCard
                    ? 'bg-white/20 text-white border-white/30'
                    : 'bg-clay-500/90 text-white border-clay-300/40'
                }`}
              >
                {hasCard ? '✓ כרטיס פעיל' : '⚠ ללא כרטיס'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* BALANCE + inline adjust. The form expands IN PLACE (doesn't replace
          the card), so closing it returns to the card, not the roster. */}
      <div className="mb-4 rounded-2xl border border-mint-200 bg-mint-50/60 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-mint-700">
              יתרה נוכחית
            </div>
            <div className="mt-0.5 text-2xl font-extrabold font-display num text-ink-900">
              {balance == null ? '…' : formatAmount(balance, currency)}
            </div>
          </div>
          {!adjusting && (
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={() => setShowHistory(true)} className="btn-secondary">
                <ClockIcon className="w-4 h-4" />
                היסטוריה
              </button>
              <button type="button" onClick={() => setAdjusting(true)} className="btn-primary">
                <BanknotesIcon className="w-4 h-4" />
                עדכן נקודות
              </button>
            </div>
          )}
        </div>
        {adjusting && (
          <div className="mt-4 pt-4 border-t border-mint-200">
            <CreditPointsForm
              campId={campId}
              studentIds={[s.id]}
              onApplied={() => setAdjusting(false)}
              onCancel={() => setAdjusting(false)}
            />
          </div>
        )}
      </div>

      {/* INFO GRID — each row is its own little card so the detail
          screen has visual rhythm instead of being one long text dump. */}
      <div className="space-y-2.5">
        <InfoRow
          Icon={IdentificationIcon}
          tone="brand"
          label="מספר כרטיס נדרים"
          value={s.cardNumber}
          ltr
          mono
          emptyLabel="לא הוקצה כרטיס"
        />
        <InfoRow
          Icon={IdentificationIcon}
          tone="joy"
          label="מספר זהות"
          value={s.nationalId}
          ltr
          mono
          emptyLabel="—"
        />
        <InfoRow
          Icon={TagIcon}
          tone="sky"
          label="מזהה חיצוני"
          value={s.externalId}
          ltr
          mono
          emptyLabel="—"
        />
        <InfoRow
          Icon={ChatBubbleIcon}
          tone="gold"
          label="הערות"
          value={s.notes}
          emptyLabel="—"
        />
        <InfoRow
          Icon={CalendarDaysIcon}
          tone="mint"
          label="נוצר"
          value={formatDate(s.createdAt)}
        />
      </div>

      {/* History opens in its own modal — triggered from the button next to
          "עדכן נקודות" above. */}

      {/* ACTIONS — primary edit + danger archive + close. The danger sits
          on the far edge so it's reachable but not the natural first tap. */}
      <div className="mt-6 pt-4 border-t border-ink-100 flex flex-wrap gap-2">
        <button type="button" onClick={onEdit} className="btn-primary flex-1 sm:flex-none">
          <PencilSquareIcon className="w-4 h-4" />
          ערוך פרטים
        </button>
        <button type="button" onClick={onClose} className="btn-secondary">
          סגור
        </button>
        <button
          type="button"
          onClick={onArchive}
          className="btn ms-auto bg-joy-50 text-joy-700 hover:bg-joy-100 border border-joy-200"
        >
          <ArchiveBoxXMarkIcon className="w-4 h-4" />
          ארכב
        </button>
      </div>
    </Modal>

      {/* History — its own modal, stacked over the card so closing it returns
          to the card (not the roster). Query is enabled only while open. */}
      <Modal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        title={`היסטוריית פעולות · ${s.firstName} ${s.lastName}`}
        maxWidth="max-w-md"
      >
        {txQ.isLoading ? (
          <Spinner label="טוען היסטוריה..." />
        ) : txQ.isError ? (
          <div className="card-danger p-3 text-sm text-joy-900">שגיאה בטעינת ההיסטוריה.</div>
        ) : txs.length === 0 ? (
          <div className="rounded-2xl border border-ink-200 bg-ink-50/50 p-4 text-sm text-ink-500 text-center">
            אין עדיין פעולות לתלמיד זה.
          </div>
        ) : (
          <ul className="rounded-2xl border border-ink-200 overflow-hidden max-h-[60vh] overflow-y-auto divide-y divide-ink-100">
            {txs.map((t) => (
              <TxRow key={t.id} tx={t} currency={currency} />
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

function ClockIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

// One transaction in the student's history. Credit = mint +; debit = clay −.
// Source line shows the device name, or the inline label for device-less
// transactions (manual credit / voucher redemption).
function TxRow({ tx, currency }) {
  const credit = tx.direction === 'CREDIT';
  const source = tx.device?.name || tx.itemLabel;
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-ink-900 truncate">{tx.itemLabel}</div>
        <div className="text-xs text-ink-500 truncate">
          {formatDateTime(tx.createdAt)}
          {tx.device?.name ? ` · ${source}` : ''}
        </div>
      </div>
      <div
        className={`text-sm font-bold num shrink-0 ${credit ? 'text-mint-700' : 'text-clay-700'}`}
        dir="ltr"
      >
        {credit ? '+' : '−'}{formatAmount(tx.amount, currency)}
      </div>
    </li>
  );
}

const TONE_TILE = {
  brand: 'bg-brand-100 text-brand-700',
  sky:   'bg-sky-100   text-sky-700',
  gold:  'bg-gold-100  text-gold-700',
  mint:  'bg-mint-100  text-mint-700',
  joy:   'bg-joy-100   text-joy-700',
};

function InfoRow({ Icon, tone, label, value, ltr, mono, emptyLabel }) {
  const empty = value == null || value === '';
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-ink-200 bg-white p-3">
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${TONE_TILE[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
          {label}
        </div>
        <div
          dir={ltr ? 'ltr' : undefined}
          className={`mt-0.5 text-sm break-words ${
            empty ? 'text-ink-400 italic' : 'text-ink-900 font-semibold'
          } ${mono && !empty ? 'font-mono num tracking-wider' : ''}`}
        >
          {empty ? emptyLabel : value}
        </div>
      </div>
    </div>
  );
}
